import {
  Injectable,
  EventEmitter,
  Inject,
  InjectionToken,
} from '@angular/core';

import { Credential, User } from './credential';

export interface IConfig {
  passwordRoute: string;
  storage: string;
  loginRoute: string;
  rootRoute: string;
  contextRoute: string;
}

export const initial: IConfig = {
  storage: 'storage',
  passwordRoute: '',
  contextRoute: '',
  loginRoute: '',
  rootRoute: '',
};

export type options = Partial<IConfig>;

export const config: InjectionToken<string> = new InjectionToken('config');

export const CONFIG: InjectionToken<string> = new InjectionToken('CONFIG');

export const INITIAL: InjectionToken<IConfig> = new InjectionToken('INITIAL');

export function _configFactory(
  initial: options,
  value: options | (() => options)
): Function | options {
  return typeof value === 'function' ? value() : { ...initial, ...value };
}

/** Antecedência com que o token é renovado antes de expirar. */
const REFRESH_MARGIN_MS = 60000;

/** Piso do agendamento, para nunca cair em um laço de renovações imediatas. */
const MIN_REFRESH_DELAY_MS = 10000;

/** Usado quando o back-end não envia um `expiresIn` utilizável. */
const FALLBACK_TTL_MS = 900000;

/** Espera antes de tentar de novo quando uma renovação falha. */
const RETRY_REFRESH_MS = 60000;

@Injectable()
export class SecurityService {
  private refreshTimeoutId: any;

  private _companyId: string;
  public set companyId(companyId: string) {
    this._companyId = companyId;
    localStorage.setItem('companyId', companyId);
  }

  public get companyId() {
    return this._companyId
      ? this._companyId
      : localStorage.getItem('companyId');
  }

  public onRefresh: EventEmitter<string>;

  public onForbidden: EventEmitter<Credential>;

  public onUnauthorized: EventEmitter<Credential>;

  private _credential: Credential;

  constructor(@Inject(config) config: IConfig) {
    this._credential = new Credential(config);
    this.onRefresh = new EventEmitter<string>();
    this.onForbidden = new EventEmitter<Credential>();
    this.onUnauthorized = new EventEmitter<Credential>();

    this.watchVisibility();
  }

  public init(user?: User): void {
    if (user) {
      // Cada credencial nova carrega o instante em que expira. É o que permite
      // decidir, ao recarregar a página ou voltar para a aba, se a sessão
      // precisa ou não ser renovada — em vez de renovar por reflexo.
      this.credential.init({ ...user, expiresAt: this.resolveExpiresAt(user) });
      localStorage.setItem('currentUser', JSON.stringify(this.credential.user));
      this.scheduleRefresh();
      return;
    }

    this.credential.init();

    if (!this.isValid()) {
      return;
    }

    // Sessão restaurada do storage: só renova de imediato se o token já estiver
    // vencido (ou perto disso). Caso contrário apenas reagenda.
    if (this.isAccessTokenExpiring()) {
      this.requestRefresh();
    } else {
      this.scheduleRefresh();
    }
  }

  public logout(): void {
    this.credential.clean();
    localStorage.removeItem('currentUser');
    localStorage.removeItem('companyId');
    this.clearRefreshTimeout();
  }

  public hasRoles(roles: string | string[]): boolean {
    if (!this.isValid()) {
      return false;
    }

    if (!roles || roles.length === 0) {
      return true;
    }

    if (this.credential.roles.length === 0) {
      return false;
    }

    let valid = false;

    if (typeof roles === 'string') {
      valid =
        this.credential.roles.filter((userRole: string) => {
          return userRole === roles;
        }).length !== 0;
    } else {
      for (let index = 0; index < roles.length; index++) {
        let role = roles[index];

        let count = this.credential.roles.filter((userRole: string) => {
          return userRole === role;
        }).length;

        if (count > 0) {
          valid = true;
          break;
        }
      }
    }
    return valid;
  }

  public invalidate(): void {
    this._credential.clean();
    localStorage.removeItem('currentUser');
    localStorage.removeItem('companyId');
    this.clearRefreshTimeout();
  }

  public isValid(): boolean {
    return this._credential.user !== undefined;
  }

  /**
   * Verdadeiro quando o access token já venceu ou vence dentro da margem de
   * renovação. Credenciais antigas, sem `expiresAt`, contam como expirando.
   */
  public isAccessTokenExpiring(margin: number = REFRESH_MARGIN_MS): boolean {
    const expiresAt = this._credential.expiresAt;
    return !expiresAt || expiresAt - Date.now() <= margin;
  }

  /**
   * Agenda a próxima renovação. Sempre cancela o agendamento anterior: antes um
   * `setInterval` era criado a cada `init(user)` e o anterior ficava órfão, o
   * que multiplicava as renovações (e os overlays de carregamento) a cada troca
   * de contexto ou login.
   */
  private scheduleRefresh(): void {
    this.clearRefreshTimeout();

    if (!this.isValid()) {
      return;
    }

    const remaining = this._credential.expiresAt - Date.now() - REFRESH_MARGIN_MS;
    const delay = Number.isFinite(remaining)
      ? Math.max(remaining, MIN_REFRESH_DELAY_MS)
      : FALLBACK_TTL_MS;

    this.refreshTimeoutId = setTimeout(() => this.requestRefresh(), delay);
  }

  /**
   * Pede a renovação e deixa um retry armado. Quem trata `onRefresh` chama
   * `init(user)` no sucesso, e isso reagenda — cancelando este retry.
   */
  private requestRefresh(): void {
    this.clearRefreshTimeout();
    this.refreshTimeoutId = setTimeout(() => this.requestRefresh(), RETRY_REFRESH_MS);
    this.onRefresh.emit(this._credential.refreshToken);
  }

  private clearRefreshTimeout(): void {
    if (this.refreshTimeoutId) {
      clearTimeout(this.refreshTimeoutId);
      this.refreshTimeoutId = undefined;
    }
  }

  private resolveExpiresAt(user: User): number {
    // `expired-password` e afins reaproveitam a credencial atual sem emitir um
    // token novo: nesse caso o vencimento original continua valendo.
    if (user.expiresAt) {
      return user.expiresAt;
    }

    const ttl = Number(user.expiresIn);

    return Number.isFinite(ttl) && ttl > 0
      ? Date.now() + ttl * 1000
      : Date.now() + FALLBACK_TTL_MS;
  }

  /**
   * Navegadores estrangulam timers de abas em segundo plano, então o
   * agendamento acordava atrasado (às vezes em rajada) quando o usuário
   * voltava. Ao reexibir a aba o estado é reavaliado pelo relógio, não pelo
   * timer: renova na hora se venceu, senão só reagenda.
   */
  private watchVisibility(): void {
    if (typeof document === 'undefined') {
      return;
    }

    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState !== 'visible' || !this.isValid()) {
        return;
      }

      if (this.isAccessTokenExpiring()) {
        this.requestRefresh();
      } else {
        this.scheduleRefresh();
      }
    });
  }

  public get isRootContext(): boolean {
    return this._credential.user?.isRoot === true;
  }

  /**
   * Indica se o usuário logado pode acessar as telas do painel. Falso apenas
   * para o agente que ainda não é gestor: ele entra somente para cadastrar a
   * própria empresa. Ausência do valor equivale a `true`.
   */
  public get hasPanelAccess(): boolean {
    return this._credential.panelAccess;
  }

  public get credential(): Credential {
    return this._credential;
  }
}
